import {
  Field,
  ImageField,
  NextImage,
  RichTextField,
  useSitecoreContext,
  Text,
  RichText,
  LinkField,
} from "@sitecore-jss/sitecore-jss-nextjs";
import clsx from "clsx";
import { ComponentProps } from "@repo/sitecore-lib/component-props";
import {
  TYPOGRAPHY_DISPLAY2,
  TYPOGRAPHY_HEADING4,
  TYPOGRAPHY_BODY3_SENTENCE,
} from "@/theme/topshop-2/typography/typography-constants";
import { appendRef } from "../../utils";
import EditorialVideo from "../video/editorial-video";
import styles from "./editorial-article.module.css";

export type EditorialArticleProps = ComponentProps & {
  fields: {
    enableSound?: Field<boolean>;
    enableControls?: Field<boolean>;
    enableAutoPlay?: Field<boolean>;
    headline: Field<string>;
    subHeadline: Field<string>;
    bodyText?: RichTextField;
    image: ImageField;
    video: Field<string>;
    ctaLink: LinkField;
    ctaRef: Field<string>;
  };
};

const EditorialArticle = (props: EditorialArticleProps): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const isEditing = sitecoreContext?.pageEditing;

  if (
    (!isEditing && !props.fields.headline.value) ||
    !props.fields.ctaLink?.value?.href
  ) {
    return <></>;
  }

  const ctaLinkWithRef = appendRef(
    props.fields.ctaLink.value.href!,
    props.fields.ctaRef.value,
  );

  return (
    <a
      href={ctaLinkWithRef}
      target={props.fields.ctaLink.value.target || "_self"}
      className={clsx(
        styles.editorialArticleContainer,
        isEditing && styles.editingSpacing,
      )}
    >
      <div className={clsx(styles.imageContainer)}>
        {props.fields.video?.value ? (
          <EditorialVideo
            {...props}
            videoUrl={props.fields.video.value}
            enableSound={props.fields.enableSound!}
            enableControls={props.fields.enableControls!}
            enableAutoPlay={props.fields.enableAutoPlay!}
            classname=""
          />
        ) : (
          <NextImage
            field={props.fields.image}
            data-testid="jss-image"
            editable
            unoptimized
          />
        )}
      </div>
      <div
        className={clsx(
          styles.textContainer,
          isEditing && styles.editingSpacing,
        )}
      >
        <Text
          tag="span"
          field={props.fields.subHeadline}
          className={clsx(TYPOGRAPHY_HEADING4)}
        />
        <Text
          tag="h2"
          field={props.fields.headline}
          className={clsx(TYPOGRAPHY_DISPLAY2)}
        />
        <RichText
          field={props.fields.bodyText}
          className={clsx(TYPOGRAPHY_BODY3_SENTENCE)}
        />
      </div>
    </a>
  );
};

export default EditorialArticle;
